import { z } from 'zod';
import { format } from 'date-fns';
import { PaymentMadeMultiInvoiceCreate } from '@/app/types/payment';

export const purchaseInvoiceAllocationSchema = z.object({
  invoice_voucher_id: z.number().positive({ message: 'Invalid bill selected' }),
  invoice_number: z.string(), // For display/reference in errors
  amount_due: z.number(), // For validation context
  amount_to_allocate: z.number()
                         .min(0, { message: 'Allocation cannot be negative' })
                         .refine((val) => /^\d+(\.\d{1,2})?$/.test(val.toString()), { // Ensure max 2 decimal places
                            message: "Max 2 decimal places allowed",
                         }),
  selected: z.boolean(), // To track which rows are part of the submission
});

export const paymentOutSchema = z.object({
  party_id: z.number({ required_error: 'Supplier is required.' }).positive(),
  paid_from_account_id: z.number({ required_error: 'Paid From Account is required.' }).positive(),
  total_payment_amount: z.number({ required_error: 'Payment Amount is required.' })
                           .gt(0, { message: 'Payment Amount must be greater than 0.' })
                           .refine((val) => /^\d+(\.\d{1,2})?$/.test(val.toString()), {
                              message: "Max 2 decimal places allowed",
                           }),
  transaction_date: z.date({ required_error: 'Payment Date is required.' }),
  reference_number: z.string().optional().nullable(),
  narration: z.string().optional().nullable(),
  // Array of allocations, refined later
  allocations: z.array(purchaseInvoiceAllocationSchema),
})
.refine(data => {
    // Validation 1: Ensure at least one allocation is selected if amount > 0
    const selectedAllocations = data.allocations.filter(alloc => alloc.selected && alloc.amount_to_allocate > 0);
    if (data.total_payment_amount > 0 && selectedAllocations.length === 0) {
      return false; // Fails validation
    }
    return true; // Passes validation
}, {
    message: 'Please select at least one bill to allocate the payment to.',
    path: ['allocations'], // Associate error with the allocations field
})
.refine(data => {
    // Validation 2: Total allocated amount cannot exceed total payment amount
    const totalAllocated = data.allocations
        .filter(alloc => alloc.selected) // Only consider selected allocations
        .reduce((sum, alloc) => sum + (alloc.amount_to_allocate || 0), 0);
    // Use a small tolerance for floating point comparisons
    return totalAllocated <= data.total_payment_amount + 0.001;
}, {
    message: 'Total allocated amount cannot exceed the Payment Amount.',
    path: ['total_payment_amount'], // Associate error with the main amount field or allocations
});


// Schema for advance payments (no allocations required)
export const advancePaymentOutSchema = z.object({
  party_id: z.number({ required_error: 'Supplier is required.' }).positive(),
  paid_from_account_id: z.number({ required_error: 'Paid From Account is required.' }).positive(),
  total_payment_amount: z.number({ required_error: 'Payment Amount is required.' })
                           .gt(0, { message: 'Payment Amount must be greater than 0.' })
                           .refine((val) => /^\d+(\.\d{1,2})?$/.test(val.toString()), {
                              message: "Max 2 decimal places allowed",
                           }),
  transaction_date: z.date({ required_error: 'Payment Date is required.' }),
  reference_number: z.string().optional().nullable(),
  narration: z.string().optional().nullable(),
  allocations: z.array(purchaseInvoiceAllocationSchema).default([]),
});

// Type for form values derived from the schema
export type PaymentOutFormValues = z.infer<typeof paymentOutSchema>;
export type PurchaseInvoiceAllocationFormValues = z.infer<typeof purchaseInvoiceAllocationSchema>;

// Function to format data for API submission
export const formatPaymentOutPayload = (data: PaymentOutFormValues): PaymentMadeMultiInvoiceCreate => {
  const selectedAllocations = data.allocations
    .filter(alloc => alloc.selected && alloc.amount_to_allocate > 0) // Only submit selected and allocated rows
    .map(alloc => ({
      invoice_voucher_id: alloc.invoice_voucher_id,
      // Ensure amount_to_allocate is a number with max 2 decimal places for the API
      amount_to_allocate: parseFloat(alloc.amount_to_allocate.toFixed(2)),
    }));

  return {
    party_id: data.party_id,
    paid_from_account_id: data.paid_from_account_id,
    total_payment_amount: parseFloat(data.total_payment_amount.toFixed(2)),
    transaction_date: format(data.transaction_date, 'yyyy-MM-dd'),
    allocations: selectedAllocations,
    reference_number: data.reference_number || null,
    narration: data.narration || null,
  };
};